import React from 'react';
import { Button, DataTable, Modal, TextInput } from 'carbon-components-react';
import * as midi from '../js/rpl_midi.js'

import { Copy16, Download16, Edit16, Paste16, Save16, Upload16 } from '@carbon/icons-react';

const {
	TableContainer,
	Table,
	TableHead,
	TableHeader,
	TableRow,
	TableBody,
	TableCell,
} = DataTable;

/*
 * DataTable to manage presets.
 *
 * numProgs - number of available presets (including "live") (default: 128).
 * liveProg - program number for "live" knob settings (1-based, optional).
 * allowRename - true to allow renaming presets (optional, default: false).
 */
class PresetTable extends React.Component {
	constructor(props) {
		super(props);
		var i;

		this.numProgs = props.numProgs || 128;

		// set default program names
		var defaultProgramNames = Array(this.numProgs);
		for (i = 0; i < this.numProgs; i++) {
			defaultProgramNames[i] = 'Preset ' + (i+1);
		}

		this.state = {
			fileDownloadUrl : null,
			fileDownloadName : "preset.syx",
			fileUploadProgramNum : -1,
			programValid : Array(props.numProgs).fill(true),	// valid by default, in case we don't hear from pedal
			programNames : defaultProgramNames,
			programClipboard : null,							// for copy/paste
			renamePresetHeading : "Rename preset",				// Modal dialog heading
			renamePresetPlaceHolder : "",						// TextInput placeholder
			renamePresetProgramNum : -1,						// which MIDI program number we are renaming`
			renamePresetValue : "",								// new preset name (contents of TextInput)
			renamePresetModalOpen : false						// set true to show dialog, false to hide
		};

		this.liveProg = props.liveProg || -1;
		this.allowRename = props.allowRename || false;

		this.initialRows = [ ];
		for (i = 0; i < this.numProgs; i++) {
			this.initialRows.push(
				{
					id: 'prog'+i,
					prognum: i,						/* 0-based */
					name: defaultProgramNames[i],	/* 1-based */
				},
			);
		}
		
		this.headers = [
		  {
			key: 'prognum',
			header: '# (Load)',
		  },
		  {
			key: 'name',
			header: 'Name',
		  }
		];

		if (this.allowRename) {
			this.headers.push(
			  {
				/* rename preset */
				key: 'rename',
				header: 'Rename',
			  }
			);
		}

		this.headers.push(
		  {
			/* save current settings to pedal */
			key: 'save',
			header: 'Write',
		  },
		  {
			/* save current settings to pedal */
			key: 'copy',
			header: 'Copy',
		  },
		  {
			/* save current settings to pedal */
			key: 'paste',
			header: 'Paste',
		  },
		  {
			/* download preset file from pedal */
			key: 'download',
			header: 'From Pedal',
		  },
		  {
			/* upload preset file to pedal */
			key: 'upload',
			header: 'To Pedal',
		  },
		);

		this.actionColumnWidth="5%";

		this.downloadPresetFile = this.downloadPresetFile.bind(this);
		this.uploadPresetFile   = this.uploadPresetFile.bind(this);
		this.openPresetFile     = this.openPresetFile.bind(this);
		this.openRenameModal	= this.openRenameModal.bind(this);
		this.renamePreset		= this.renamePreset.bind(this);
		this.savePreset			= this.savePreset.bind(this);
		this.notifyProgramName  = this.notifyProgramName.bind(this);
	}

	/*
	 * Subscribe to property updates when the PresetTable is loaded.
	 *
	 * componentDidMount() is a hook that gets invoked right after a
	 * React component has been mounted (after the first render()).
	 * If the component is unmounted and remounted, we will get a
	 * second call.  An alternative is a useEffect hook.
	 */
	componentDidMount() {
		midi.addProgramSubscriber(this);
	}

	/*
	 * Ignore property updates when the PresetTable is unloaded.
	 */
	componentWillUnmount() {
		midi.removeProgramSubscriber(this);
	}

	/*
	 * Called when the pedal sends a program name response
	 */
	notifyProgramName(bank, prog, name) {
		if (bank === 0 && prog < this.numProgs) {
			let programNames = this.state.programNames;

			if (name !== "") {
				programNames[prog] = name;
			} else {
				programNames[prog] = 'Preset ' + (prog+1);
			}
			this.setState({programNames});
		}
	}

	/*
	 * Called when pedal sends a "program valid" response.
	 */
	notifyProgramValid(bank, prog, valid) {
		if (bank === 0 && prog < this.numProgs) {
			let programValid = this.state.programValid;
			programValid[prog] = valid ? true : false;
			this.setState({programValid});

			if (valid === true) {
				// retrieve the program name
				var target = this;
				midi.getProgramName(bank, prog,
									function(name) {
										target.notifyProgramName(bank, prog, name);
									});
			}
		}
	}

	/*
	 * Download a preset file from the pedal to the local computer.
	 *
	 * progNum - MIDI program number (0-based).
	 * progName - human-readable name.
	 */
	downloadPresetFile(progNum, progName, callback) {
		/*
		 * Send a SysEx command to the pedal to download the preset.
		 * When program data is returned by the pedal, our callback
		 * will create a blob and download the data as a file.
		 */
		const bank = 0;
		var target = this;
		midi.getProgramData(bank, progNum,
							function(presetData) {
								const blob = new Blob([presetData]);
								const fileDownloadUrl = URL.createObjectURL(blob);
								const fileDownloadName = progName + ".syx"
								console.log("filename: \"" + fileDownloadName + "\"");

								target.setState({
												fileDownloadUrl  : fileDownloadUrl,
												fileDownloadName : fileDownloadName
											  },
									() => {
										target.doFileDownload.click();
										URL.revokeObjectURL(fileDownloadUrl); // free up storage
										target.setState({fileDownloadUrl : ""});
								});
							});

	}

	/*
	 * Upload a preset file from the pedal to the local computer.
	 * Stores the MIDI program number for the selected row and
	 * clicks a hidden input tag to open the file dialog.
	 * When the file is uploaded, openPresetFile() will be called.
	 *
	 * progNum - MIDI program number (0-based).
	 */
	uploadPresetFile(progNum) {
		console.log("Uploading program " + progNum);
		this.setState({ fileUploadProgramNum : progNum });
		this.doFileUpload.click();
	}

	/*
	 * Process a preset file that has been selected by the user
	 * and uploaded.  This happens within the React app,
	 * it is not uploaded to the server.
	 *
	 * progNum - MIDI program number (0-based).
	 */
	openPresetFile(evt, progNum) {
		const fileObj = evt.target.files[0];
		const reader = new FileReader();

		let fileloaded = e => {
			// e.target.result is the file's content as text
			const fileContents = e.target.result;
			const data = new Uint8Array(fileContents.length);
			for (var i = 0; i < fileContents.length; i++) {
				data[i] = fileContents.charCodeAt(i);
			}

			console.log("File name: " + fileObj.name + " (" + fileContents.length + " bytes)");

			const bank = 0;
			midi.setProgramData(bank, progNum, data);

			// when notifyProgramValid() is called, it will ask for the preset name
			setTimeout(function(){ midi.getIsProgramValid(bank, progNum); }, 3000);
		}

		fileloaded = fileloaded.bind(this);
		reader.onload = fileloaded;

		if (fileObj) {
			reader.readAsBinaryString(fileObj);
		}

		// clear the stored MIDI program number
		this.setState({ fileUploadProgramNum : -1 });

		// reset the dialog value so that we get notified if the same file
		// is selected again.
		evt.target.value = null;
	}

	/*
	 * Open Rename Preset modal dialog
	 *
	 * progNum - MIDI program number (0-based).
	 * progName - human-readable name.
	 */
	openRenameModal(progNum, progName) {
		this.setState({ renamePresetHeading : "Rename preset " + (progNum + 1),
						renamePresetPlaceholder : progName,
						renamePresetProgramNum : progNum,
						renamePresetValue : progName,	// initialize to old name
						renamePresetModalOpen : true });
	}

	/*
	 * Rename a preset on the pedal.
	 *
	 * progNum - MIDI program number (0-based).
	 * newName - new preset name
	 */
	renamePreset(progNum, newName) {
		let progNames = this.state.programNames;
		progNames[progNum] = newName;

		this.setState({ renamePresetModalOpen : false,
						programNames : progNames });

		if (newName !== "") {
			midi.setProgramName(0/*bank*/, progNum, newName);
		}
	}

	/*
	 * Write current settings to a program slot on the pedal.
	 * Refreshes program valid state and name.
	 *
	 * progNum - MIDI program number (0-based).
	 */
	savePreset(progNum) {
		midi.saveProgram(progNum);

		setTimeout(function(){ midi.getIsProgramValid(0, progNum); }, 3000);
	}

	/*
	 * Copy a preset from the pedal to the clipboard.
	 *
	 * progNum - MIDI program number (0-based).
	 */
	copyPreset(progNum) {
		/*
		 * Send a SysEx command to the pedal to download the preset.
		 * When program data is returned by the pedal, our callback
		 * will store it in the clipboard (this.state.programClipboard).
		 */
		console.log("copying program " + progNum);
		const bank = 0;
		var target = this;
		midi.getProgramData(bank, progNum,
							function(presetData) {
								target.setState({programClipboard : presetData});
								console.log("program copied to clipboard");
							});

	}

	/*
	 * Paste preset from clipboard, uploading it to the pedal
	 * at the selected program location.
	 *
	 * progNum - MIDI program number (0-based).
	 */
	pastePreset(progNum) {
		if (this.state.programClipboard != null) {
			midi.setProgramData(0, progNum, this.state.programClipboard);
			console.log("pasted clipboard to program " + progNum);

			// when notifyProgramValid() is called, it will ask for the preset name
			setTimeout(function(){ midi.getIsProgramValid(0, progNum); }, 3000);
		}
	}

	/*
	 * Rename column is only included if pedal allows preset names.
	 *
	 * progNum - MIDI program number (0-based).
	 * progName - human-readable name.
	 * disabled - whether the button should be disabled.
	 */
	conditionallyGenerateRenameColumn(progNum, progName, disabled) {
		if (this.allowRename) {
			return (
										<TableCell width={this.actionColumnWidth}>
											<Button
												disabled={disabled}
												hasIconOnly={true}
												iconDescription="Edit preset name"
												kind="ghost"
												onClick={() => this.openRenameModal(progNum, progName)}
												onFocus={function noRefCheck(){}}
												renderIcon={Edit16}
												size="small"
												tooltipAlignment="center"
												tooltipPosition="top"
												type="button"
											/>
										</TableCell>
			);
		}
	}

	render() {
		return (
			<DataTable
				rows={this.initialRows}
				headers={this.headers}
				render={({ rows, headers, getHeaderProps }) => (
					<TableContainer title="Presets">
						<Table>
							<TableHead>
								<TableRow>
									{headers.map(header => (
										<TableHeader {...getHeaderProps({ header })}>
										{header.header}
										</TableHeader>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map(row => (
									<TableRow key={row.id}>
									{/* Don't automatically generate cells
										{row.cells.map(cell => (
											<TableCell key={cell.id}
												onClick={function noRefCheck(){midi.saveProgram(row.cells.find(cell => cell.id==row.id+':prognum').value-1)}}
											>{cell.value}</TableCell>
										))}
									*/}
										<TableCell width="10%">
											<Button
												disabled={!this.state.programValid[(row.cells.find(cell => cell.id===row.id+':prognum').value)]}
												kind="tertiary"
												onClick={function noRefCheck(){midi.sendProgramChange(row.cells.find(cell => cell.id===row.id+':prognum').value)}}
												onFocus={function noRefCheck(){}}
												size="small"
												tooltipAlignment="center"
												tooltipPosition="top"
												type="button"
											>
											{row.cells.find(cell => cell.id===row.id+':prognum').value + 1}
											</Button>
										</TableCell>
										<TableCell
											onClick={function noRefCheck(){midi.sendProgramChange(row.cells.find(cell => cell.id===row.id+':prognum').value)}}
										>
										{this.state.programNames[row.cells.find(cell => cell.id===row.id+':prognum').value]}
										</TableCell>
										{this.conditionallyGenerateRenameColumn(row.cells.find(cell => cell.id===row.id+':prognum').value, this.state.programNames[row.cells.find(cell => cell.id===row.id+':prognum').value], !this.state.programValid[(row.cells.find(cell => cell.id===row.id+':prognum').value)])}
										<TableCell width={this.actionColumnWidth}>
											<Button
												disabled={false}
												hasIconOnly={true}
												iconDescription="Save current settings to pedal"
												kind="ghost"
												onClick={() => this.savePreset(row.cells.find(cell => cell.id===row.id+':prognum').value)}
												onFocus={function noRefCheck(){}}
												renderIcon={Save16}
												size="small"
												tooltipAlignment="center"
												tooltipPosition="top"
												type="button"
											/>
										</TableCell>
										<TableCell width={this.actionColumnWidth}>
											<Button
												disabled={!this.state.programValid[(row.cells.find(cell => cell.id===row.id+':prognum').value)]}
												hasIconOnly={true}
												iconDescription="Copy preset"
												kind="ghost"
												onClick={() => this.copyPreset(row.cells.find(cell => cell.id===row.id+':prognum').value)}
												onFocus={function noRefCheck(){}}
												renderIcon={Copy16}
												size="small"
												tooltipAlignment="center"
												tooltipPosition="top"
												type="button"
											/>
										</TableCell>
										<TableCell width={this.actionColumnWidth}>
											<Button
												disabled={this.state.programClipboard === null}
												hasIconOnly={true}
												iconDescription="Paste preset"
												kind="ghost"
												onClick={() => this.pastePreset(row.cells.find(cell => cell.id===row.id+':prognum').value)}
												onFocus={function noRefCheck(){}}
												renderIcon={Paste16}
												size="small"
												tooltipAlignment="center"
												tooltipPosition="top"
												type="button"
											/>
										</TableCell>
										<TableCell width={this.actionColumnWidth}>
											<Button
												disabled={!this.state.programValid[(row.cells.find(cell => cell.id===row.id+':prognum').value)]}
												hasIconOnly={true}
												iconDescription="Download preset to file"
												kind="ghost"
												onClick={() => this.downloadPresetFile(row.cells.find(cell => cell.id===row.id+':prognum').value, this.state.programNames[row.cells.find(cell => cell.id===row.id+':prognum').value])}
												onFocus={function noRefCheck(){}}
												renderIcon={Download16}
												size="small"
												tooltipAlignment="center"
												tooltipPosition="top"
												type="button"
											/>
										</TableCell>
										<TableCell width={this.actionColumnWidth}>
											<Button
												disabled={false}
												hasIconOnly={true}
												iconDescription="Upload preset file to pedal"
												kind="ghost"
												onClick={() => this.uploadPresetFile(row.cells.find(cell => cell.id===row.id+':prognum').value, row.cells.find(cell => cell.id===row.id+':name').value)}
												onFocus={function noRefCheck(){}}
												renderIcon={Upload16}
												size="small"
												tooltipAlignment="center"
												tooltipPosition="top"
												type="button"
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
						<a hidden
             				download={this.state.fileDownloadName}
            				href={this.state.fileDownloadUrl}
           					ref={e=>this.doFileDownload = e}
          				>download it</a>
						<input hidden
							type="file"
							multiple={false}
							accept=".syx,.bin"
							onChange={evt => this.openPresetFile(evt, this.state.fileUploadProgramNum)}
							ref={e=>this.doFileUpload = e}
						/>
						<Modal
							open={this.state.renamePresetModalOpen}
							modalHeading={this.state.renamePresetHeading}
							primaryButtonText="Rename"
							secondaryButtonText="Cancel"
							onRequestClose={() => this.setState({renamePresetModalOpen : false})}
							onRequestSubmit={() => this.renamePreset(this.state.renamePresetProgramNum, this.state.renamePresetValue)}
							shouldSubmitOnEnter={true}
						>
						<TextInput
							data-modal-primary-focus
							id="new-preset-name"
							labelText="Name"
							placeholder={this.state.renamePresetPlaceholder}
							defaultValue={this.state.renamePresetValue}
							value={this.state.renamePresetValue}
							onChange={(evt) => this.setState({ renamePresetValue : evt.target.value })}
						/>
						</Modal>
					</TableContainer>
				)}
			/>
		);
	}
}

export default PresetTable;
